import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  LinearProgress,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  ArrowBack,
  ThumbUp,
  ThumbDown,
  Person,
  EventNote,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useAppDispatch } from "../../redux/store";
import {
  approveSchedule,
  rejectSchedule,
  markSaving,
  markSaved,
  setError,
} from "../../redux/features/scheduleUpload/scheduleUploadSlice";
import { submitApprovedSchedule } from "../../network/scheduleUploadApi";
import { prepareScheduleImportPayload } from "../../utils/prepareScheduleImportPayload";
import { getShiftCategory, SHIFT_COLORS, ScheduleRow } from "../../utils/parseScheduleExcel";
import { UnmatchedEmployeeWarning } from "../../components/ScheduleUpload";
import { StepIndicator } from "../ScheduleUpload";

// ─── Mini shift chip used inside the approval cards ──────────────────────────

const MiniShift: React.FC<{ value: string }> = ({ value }) => {
  const cat = getShiftCategory(value);
  const s = SHIFT_COLORS[cat];
  return (
    <Tooltip title={s.label} arrow placement="top">
      <Box
        sx={{
          px: 0.8,
          py: 0.2,
          borderRadius: 1,
          background: s.bg,
          color: s.text,
          fontSize: 10,
          fontWeight: 600,
          border: `1px solid ${s.borderColor}`,
          whiteSpace: "nowrap",
          textAlign: "center",
          minWidth: 36,
        }}
      >
        {cat === "empty" ? "—" : value}
      </Box>
    </Tooltip>
  );
};

// ─── Compact employee card ────────────────────────────────────────────────────

const EmployeeCard: React.FC<{ row: ScheduleRow; weekIndex: number }> = ({ row, weekIndex }) => {
  const week = row.weeks[weekIndex];
  const initials = row.employee
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        background: "#fafbfc",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
      }}
    >
      {/* Header row */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: 13,
            fontWeight: 700,
            background: "linear-gradient(135deg, #1976d2, #0d47a1)",
          }}
        >
          {initials}
        </Avatar>
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={700} noWrap title={row.employee}>
            {row.employee}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.role}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {/* Day chips for the selected week */}
      <Stack direction="row" flexWrap="wrap" gap={0.6}>
        {week?.days.map((d, di) => (
          <Box key={di} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: 9, color: "text.secondary", mb: 0.3, fontWeight: 600 }}>
              {d.day}
            </Typography>
            <MiniShift value={d.value} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

// ─── Rejection dialog ─────────────────────────────────────────────────────────

const RejectDialog: React.FC<{
  open: boolean;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}> = ({ open, onConfirm, onCancel }) => {
  const [note, setNote] = useState("");
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "error.main", fontWeight: 700 }}>Reject Schedule</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          Rejecting will discard this submission. Add an optional note for the uploader.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Rejection note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Please fix shift conflicts before re-submitting."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">Cancel</Button>
        <Button
          onClick={() => { onConfirm(note); setNote(""); }}
          color="error"
          variant="contained"
          startIcon={<ThumbDown />}
          sx={{ borderRadius: 50 }}
        >
          Confirm Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Success view ─────────────────────────────────────────────────────────────

const SuccessView: React.FC<{
  approvedBy: string;
  approvedAt: string;
  totalEmployees: number;
  onReset: () => void;
}> = ({ approvedBy, approvedAt, totalEmployees, onReset }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ maxWidth: 500, mx: "auto", mt: 10, textAlign: "center", px: 3 }}>
      <CheckCircle sx={{ fontSize: 72, color: "success.main" }} />
      <Typography variant="h4" fontWeight={800} mt={2} gutterBottom>
        Schedule Saved!
      </Typography>
      <Typography color="text.secondary">
        {totalEmployees} employee schedules submitted successfully.
      </Typography>
      <Paper
        elevation={0}
        sx={{
          mt: 3, p: 3, borderRadius: 3, textAlign: "left",
          border: "1px solid", borderColor: "success.light", background: "#f1f8f1",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Person sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2"><strong>Approved by:</strong> {approvedBy}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <EventNote sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2">
              <strong>Approved at:</strong> {new Date(approvedAt).toLocaleString()}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
      <Stack direction="row" spacing={2} mt={3} justifyContent="center">
        <Button variant="outlined" onClick={() => navigate("/Dashboard")} sx={{ borderRadius: 50 }}>
          Go to Dashboard
        </Button>
        <Button variant="contained" onClick={onReset} sx={{ borderRadius: 50 }}>
          Upload New Schedule
        </Button>
      </Stack>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ManagerApprovalPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useSelector((state: RootState) => state.user.user);
  const uploadState = useSelector((state: RootState) => state.scheduleUpload);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [activeWeek, setActiveWeek] = useState(0);

  const managerName =
    `${user?.osEmpFirstName ?? (user as any)?.os_emp_first_name ?? ""} ${user?.osEmpLastName ?? (user as any)?.os_emp_last_name ?? ""}`.trim() || "Manager";

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!uploadState.parsedData) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 10, textAlign: "center", px: 3 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No schedule data found. Please start from the Upload page.
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/schedule-upload")} sx={{ mt: 2 }}>
          Back to Upload
        </Button>
      </Box>
    );
  }

  // ── Saved state ───────────────────────────────────────────────────────────
  if (uploadState.uploadStatus === "saved") {
    return (
      <SuccessView
        approvedBy={uploadState.approvedBy!}
        approvedAt={uploadState.approvedAt!}
        totalEmployees={uploadState.parsedData.rows.length}
        onReset={() => {
          dispatch({ type: "scheduleUpload/resetUpload" });
          navigate("/schedule-upload");
        }}
      />
    );
  }

  const handleApprove = async () => {
    dispatch(approveSchedule({ approvedBy: managerName }));
    dispatch(markSaving());
    try {
      const approvedAt = new Date().toISOString();
      const shiftRows = prepareScheduleImportPayload(uploadState.parsedData!);
      if (shiftRows.length === 0) {
        dispatch(setError("No valid schedule rows were generated. Check employee matches and shift values (examples: 07:00, 07:00-15:00, 7:00 AM, Leave)."));
        return;
      }
      const result = await submitApprovedSchedule(
        shiftRows,
        managerName,
        approvedAt,
        (user as any)?.employee_id
      );
      if (result.success) {
        dispatch(markSaved());
      } else {
        dispatch(setError(result.message));
      }
    } catch (err: any) {
      dispatch(setError(err.message || "Failed to save schedule."));
    }
  };

  const handleReject = (note: string) => {
    dispatch(rejectSchedule(note || null));
    setRejectDialogOpen(false);
    navigate("/schedule-upload");
  };

  const { parsedData } = uploadState;
  const isSaving = uploadState.uploadStatus === "saving";
  const weekOptions = parsedData.rows[0]?.weeks ?? [];

  return (
    <Box sx={{ maxWidth: "100%", px: { xs: 2, md: 4 }, py: 4 }}>
      {/* ── Header ── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "flex-start" }} spacing={2} mb={1}>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={800}>Manager Approval</Typography>
          <Typography variant="body2" color="text.secondary">
            {parsedData.fileName} · {parsedData.rows.length} employees · Review and approve to save
          </Typography>
        </Box>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          color="inherit"
          onClick={() => navigate("/schedule-upload/review")}
          disabled={isSaving}
          sx={{ borderRadius: 50 }}
        >
          Back to Review
        </Button>
      </Stack>

      <StepIndicator active={2} />

      {isSaving && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
      {uploadState.errorMessage && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{uploadState.errorMessage}</Alert>
      )}

      <UnmatchedEmployeeWarning names={uploadState.unmatchedEmployees} />

      {/* ── Week selector tabs ── */}
      <Stack direction="row" spacing={1} mt={2} mb={2.5}>
        {weekOptions.map((w, i) => (
          <Button
            key={i}
            size="small"
            variant={i === activeWeek ? "contained" : "outlined"}
            color={i === activeWeek ? "primary" : "inherit"}
            onClick={() => setActiveWeek(i)}
            sx={{ borderRadius: 50, textTransform: "none", fontWeight: 600, fontSize: 12 }}
          >
            {w.weekLabel}
          </Button>
        ))}
      </Stack>

      {/* ── Employee cards grid ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        {parsedData.rows.map((row) => (
          <EmployeeCard key={row.id} row={row} weekIndex={activeWeek} />
        ))}
      </Box>

      {/* ── Action bar ── */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={700}>
            Approving as:{" "}
            <span style={{ color: "#1976d2" }}>{managerName}</span>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Approval will be timestamped and saved to the database
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ThumbDown />}
            onClick={() => setRejectDialogOpen(true)}
            disabled={isSaving}
            sx={{ borderRadius: 50, px: 3 }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={
              isSaving
                ? <CircularProgress size={16} color="inherit" />
                : <ThumbUp />
            }
            onClick={handleApprove}
            disabled={isSaving}
            sx={{ borderRadius: 50, px: 4, fontWeight: 700 }}
          >
            {isSaving ? "Saving…" : "Approve & Save"}
          </Button>
        </Stack>
      </Paper>

      <RejectDialog
        open={rejectDialogOpen}
        onConfirm={handleReject}
        onCancel={() => setRejectDialogOpen(false)}
      />
    </Box>
  );
};

export default ManagerApprovalPage;
